/**
 * Abort-aware pieces for the invoke → state → result poll loops that run
 * inside a React Query `queryFn` (connected signals, coverage last-fired).
 * React Query aborts its `signal` when the query is unmounted or its key
 * changes; these make the loop stop there instead of polling a job nobody
 * reads any more.
 */

function abortError(signal: AbortSignal): unknown {
  return signal.reason ?? new DOMException('The operation was aborted.', 'AbortError')
}

/** Throws the abort reason when `signal` has fired. */
export function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw abortError(signal)
}

/** Waits `ms`; rejects with the abort reason as soon as `signal` fires. */
export function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(abortError(signal))
    const onAbort = () => {
      clearTimeout(t)
      reject(abortError(signal!))
    }
    const t = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}
